import React, { FC, Fragment, useCallback, useContext, useState } from 'react';
import { OrderBy, OrderDirection } from 'models/search-result';
import { useQuery, NetworkStatus } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import ClinicPageActions from 'components/clinics/clinic-page-actions/clinic-page-actions.component';
import ClinicList from 'components/clinics/clinic-list/clinic-list.component';
import Loader from 'components/shared/loader/loader.component';
import AddClinicDialog from 'components/clinics/add-edit-clinic/add-clinic-dialog.component';
import { Organization } from 'models/organization';
import EditClinicDialog from 'components/clinics/add-edit-clinic/edit-clinic-dialog.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import SyncHitHoriozonsDialog from 'components/shared/sync-hit-horiozons-dialog/sync-hit-horiozons-dialog.component';
import { parentClinicsTemplate } from 'apollo/templates/clinic';
import { Clinic } from 'models/clinic';
import { OrganizationListFilter } from 'models/common';

const AdminClinics: FC = () => {
    const { t: translate } = useTranslation(['navigation', 'organization']);
    const [filter, setFilter] = useState<OrganizationListFilter>({
        freeText: '',
        active: true,
        local: null,
        isWhiteLabeled: null,
    });
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isHHSyncDialogOpen, setIsHHSyncDialogOpen] = useState(false);
    const [isLocalClinic, setIsLocalClinic] = useState(false);
    const [lastOffset, setLastOffset] = useState(0);
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: 'name',
        orderDirection: OrderDirection.asc,
    });
    const [currentClinic, setCurrentClinic] = useState<Clinic>();
    const [childClinicParent, setChildClinicParent] = useState<Clinic>();
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const {
        data: { parentClinics } = {},
        loading: clinicsLoading,
        refetch,
        fetchMore,
        networkStatus,
    } = useQuery(parentClinicsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: filter,
            limit: 25,
            offset: 0,
            orderBy: orderBy.orderBy,
            orderDirection: orderBy.orderDirection,
            includeChildClinics: true,
            includeLocalClinics: true,
        },
        onCompleted: (data) => {
            setLastOffset(data?.parentClinics?.length ?? 0);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFetchMore = () => {
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        });
    };

    const handleFilterChange = useCallback(
        debounce(({ freeText, active, local, isWhiteLabeled }: OrganizationListFilter) => {
            setLastOffset(0);
            setFilter({ freeText: freeText.toLocaleLowerCase(), active, local, isWhiteLabeled });
        }, 300),
        [],
    );

    const handleOpenAddDialog = () => {
        setIsAddDialogOpen(true);
        setIsLocalClinic(false);
    };

    const handleLocalClinicDialogOpen = () => {
        setIsLocalClinic(true);
        setIsAddDialogOpen(true);
    };

    const handleAddChildClinicClicked = (clinic: Clinic) => {
        setChildClinicParent(clinic);
    };

    const handleOpenEditClicked = (clinic: Clinic) => {
        setCurrentClinic(clinic);
        setIsEditDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setCurrentClinic(undefined);
        setChildClinicParent(undefined);
        setIsAddDialogOpen(false);
        setIsEditDialogOpen(false);
        setIsHHSyncDialogOpen(false);
        setIsLocalClinic(false);
    };

    const setOrderOnField = (field: string) => {
        const direction = orderBy.orderDirection === OrderDirection.asc ? OrderDirection.desc : OrderDirection.asc;
        setOrderBy({
            orderBy: field,
            orderDirection: direction,
        });
        refetch({
            filters: filter,
            offset: 0,
            orderBy: field,
            orderDirection: direction,
        });
        setLastOffset(0);
    };

    const handleClinicAdded = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('organization:CLINIC_ADDED_MESSAGE'),
        });
        setIsAddDialogOpen(false);
        setChildClinicParent(undefined);
        setLastOffset(0);
        refetch({ filters: filter, offset: 0, orderBy: orderBy.orderBy, orderDirection: orderBy.orderDirection });
    };

    const handleClinicModified = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('organization:CLINIC_UPDATED_MESSAGE'),
        });
        setIsEditDialogOpen(false);
        setLastOffset(0);
        refetch({ filters: filter, offset: 0, orderBy: orderBy.orderBy, orderDirection: orderBy.orderDirection });
    };

    const handleHHSyncClicked = (clinic: Clinic) => {
        setCurrentClinic(clinic);
        setIsHHSyncDialogOpen(true);
    };

    const handleClinicSynced = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('organization:ORGANIZATION_SYNCED_HIT_HORIZONS'),
        });
        setIsHHSyncDialogOpen(false);
        refetch();
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('navigation:CLINICS')} />
            <ClinicPageActions
                onFilterChange={handleFilterChange}
                onAddClinicDialogOpen={handleOpenAddDialog}
                onAddLocalClinicDialogOpen={handleLocalClinicDialogOpen}
            />
            {clinicsLoading && networkStatus !== NetworkStatus.fetchMore && networkStatus !== NetworkStatus.refetch ? (
                <Loader />
            ) : (
                <ClinicList
                    filteredClinics={parentClinics}
                    onFetchMore={handleFetchMore}
                    onSetOrderOnField={setOrderOnField}
                    orderBy={orderBy}
                    onEditClinicClick={handleOpenEditClicked}
                    onHHSyncClick={handleHHSyncClicked}
                    onAddChildClinicClick={handleAddChildClinicClicked}
                />
            )}
            {isAddDialogOpen && (
                <AddClinicDialog
                    dialogOpen={isAddDialogOpen}
                    onCloseDialog={handleCloseDialog}
                    onClinicAdded={handleClinicAdded}
                    allowFullEdit
                    isLocalClinic={isLocalClinic}
                />
            )}
            {/*
                Separate instance from the dialog above rather than another mode on isAddDialogOpen:
                AddClinicDialog seeds its first wizard step from isChildClinic via useState, so
                isChildClinic has to stay constant for the lifetime of the instance. The parent comes
                from the clicked row instead of the logged-in org, and mounting only while open means
                each open starts from a clean wizard rather than relying on resetForm.
            */}
            {childClinicParent && (
                <AddClinicDialog
                    dialogOpen={!!childClinicParent}
                    parentClinic={childClinicParent}
                    onCloseDialog={handleCloseDialog}
                    onClinicAdded={handleClinicAdded}
                    isChildClinic
                />
            )}
            {isEditDialogOpen && (
                <EditClinicDialog
                    dialogOpen={isEditDialogOpen}
                    clinic={currentClinic}
                    onCloseDialog={handleCloseDialog}
                    onClinicModified={handleClinicModified}
                />
            )}
            {isHHSyncDialogOpen && (
                <SyncHitHoriozonsDialog
                    organization={currentClinic as Organization}
                    dialogOpen={isHHSyncDialogOpen}
                    onCloseDialog={handleCloseDialog}
                    onOrganizationModified={handleClinicSynced}
                />
            )}
        </Fragment>
    );
};

export default AdminClinics;
